import { deleteDevice, getDevice, getDeviceGroup, useGetDeviceStatusTemplate, useGetProductList, useProductTypeList } from '@/api';
import { AdvanceBreadcrumb } from '@/components/Breadcrumb';
import { useTableRequest } from '@/hooks';
import { AppstoreOutlined, GlobalOutlined, PlusOutlined, ReloadOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { Badge, Button, Card, Cascader, Col, Empty, Form, Input, message, Pagination, Popconfirm, Radio, Row, Select, Space, Spin, Table, Typography } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import { AddModal, AddModalRef } from './AddModal';
import DeviceCard from './DeviceCard';
import { EditModal, EditModalRef } from './EditModal';
// import { DeviceSyncModal, DeviceSyncModalRef } from './DeviceSyncModal';
import { DevicesType } from '@/api/devices';
import useAuthButton from '@/hooks/useAuthButton';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

const { Text } = Typography;
const { useMessage } = message;

export interface DevicesManageProps {}

const DevicesManage: React.FC<DevicesManageProps> = () => {
  const [match] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams<{ id?: string }>();

  // 根据当前路径判断视图模式
  const getInitialViewMode = (): 'list' | 'card' | 'map' => {
    if (location.pathname.includes('/device/map')) {
      return 'map';
    }
    return 'card';
  };

  const [viewMode, setViewMode] = useState<'list' | 'card' | 'map'>(getInitialViewMode());

  // 当路径变化时，更新视图模式
  useEffect(() => {
    if (location.pathname.includes('/device/map')) {
      setViewMode('map');
    } else if (viewMode === 'map') {
      // 如果从地图页面返回，恢复为卡片视图
      setViewMode('card');
    }
  }, [location.pathname]);

  useEffect(() => {
    match.has('create') && addModalRef.current?.open();
  }, []);

  const addModalRef = useRef<AddModalRef>(null);
  const editModalRef = useRef<EditModalRef>(null);
  // const deviceSyncModalRef = useRef<DeviceSyncModalRef>(null);

  const [messageApi, contextHolder] = useMessage();

  const { loading, form, dataSource, pagination, reload, search, reset } = useTableRequest<DevicesType>({
    onRequest: (data) => {
      console.log(data);
      return getDevice({ ...data, product_id: data.product_id || params.id, device_group_id: data.device_group_id?.[data.device_group_id.length - 1] || '' });
    },
    defaultPagination: { current: 1, pageSize: 12 },
  });

  const { data: statusOptions } = useGetDeviceStatusTemplate();
  const { data: productListOptions } = useGetProductList({ isAll: true });
  const { data: useProductTypeOptions } = useProductTypeList();

  const { checkPermissionDisabled } = useAuthButton();

  // 如果 URL 上带有查询参数，则回填到表单并触发一次查询
  useEffect(() => {
    const keys = ['product_id', 'product_type', 'status', 'name'];
    const searchObj: Record<string, string> = {};
    keys.forEach((k) => {
      const v = match.get(k);
      if (v !== null) searchObj[k] = v;
    });
    if (Object.keys(searchObj).length) {
      form.setFieldsValue({
        product_id: searchObj.product_id ?? params?.id ?? '',
        product_type: searchObj.product_type ?? '',
        status: searchObj.status ?? '',
        name: searchObj.name ?? '',
      });
      // 等待表单完成赋值后再触发加载，避免竞态
      setTimeout(() => reload(), 0);
    }
  }, []);
  const { data: deviceGroupData } = useQuery({
    queryKey: ['deviceGroup'],
    queryFn: () => getDeviceGroup(),
    select: (data) => data.result?.[0]?.children,
  });
  return (
    <>
      {contextHolder}
      <AddModal ref={addModalRef} onFinish={reload} />
      <EditModal ref={editModalRef} onFinish={reload} />
      {/* <DeviceSyncModal
        ref={deviceSyncModalRef}
        options={deviceServersOptions}
        onFinish={() => {
          reload();
        }}
      /> */}
      <AdvanceBreadcrumb title="设备管理" describe="物理设备要连接到平台，需要先在平台创建设备(支持单个或批量导入创建)" background={require('@/assets/images/product-background.png')} />
      <Card className="m20" size="small">
        <Form
          layout="inline"
          form={form}
          initialValues={{
            product_id: params?.id || '',
            platform: '',
            status: '',
            name: '',
            product_type: '',
            device_group_id: [''],
          }}
        >
          <Form.Item key="product_id" name="product_id">
            <Select
              style={{ width: 180 }}
              placeholder="产品范围"
              showSearch
              optionFilterProp="label"
              options={[{ label: '产品范围 (全部)', value: '' }, ...productListOptions.map((i) => ({ label: i.name, value: i.id }))]}
            />
          </Form.Item>
          {/* <Form.Item key="product_type" name="product_type">
            <Select style={{ width: 180 }} placeholder="产品类型" options={[{ label: '产品类型 (全部)', value: '' }, ...useProductTypeOptions.map((i) => ({ label: i, value: i }))]} />
          </Form.Item> */}
          <Form.Item key="device_group_id" name="device_group_id">
            <Cascader
              options={[{ name: '设备分组 (全部)', id: '', children: deviceGroupData }]}
              fieldNames={{
                label: 'name',
                value: 'id',
                children: 'children',
              }}
              expandTrigger="hover"
              changeOnSelect
              displayRender={(labels) => labels.reverse()[0]}
              style={{ width: 180 }}
            ></Cascader>
            {/* <Select style={{ width: 180 }} placeholder="设备分组" options={[{ label: '设备分组 (全部)', value: '' }, ...useProductTypeOptions.map((i) => ({ label: i, value: i }))]} /> */}
          </Form.Item>
          <Form.Item key="status" name="status">
            <Select style={{ width: 180 }} placeholder="状态" options={[{ label: '状态 (全部)', value: '' }, ...statusOptions.map((i) => ({ label: i, value: i }))]} />
          </Form.Item>
          <Form.Item key="name" name="name">
            <Input placeholder="设备名称"></Input>
          </Form.Item>
          <Form.Item>
            <Button type="primary" onClick={search} disabled={checkPermissionDisabled('gateway:device:query')}>
              查询
            </Button>
          </Form.Item>
          <Form.Item>
            <Button type="default" onClick={reset}>
              重置
            </Button>
          </Form.Item>
        </Form>
      </Card>
      <Card className="m20" size="small">
        <Space className="mb15 justify-space-between">
          <div>
            <Button
              type="primary"
              disabled={checkPermissionDisabled('gateway:device:add')}
              onClick={() => {
                addModalRef.current?.open();
              }}
            >
              <PlusOutlined />
              添加设备
            </Button>
          </div>
          <div className="flex-center">
            <Radio.Group
              value={viewMode}
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'map') {
                  navigate({ pathname: '/gateway/device/map', search: '?tab=cluster' });
                } else {
                  // 如果当前在地图页面，跳转回设备管理页面
                  if (location.pathname.includes('/device/map')) {
                    navigate({ pathname: '/gateway/device/manage' });
                  }
                  setViewMode(value);
                }
              }}
            >
              <Radio.Button value="card" style={{ lineHeight: '34px' }}>
                <AppstoreOutlined style={{ fontSize: '16px' }} />
              </Radio.Button>
              <Radio.Button value="list" style={{ lineHeight: '34px' }}>
                <UnorderedListOutlined style={{ fontSize: '16px' }} />
              </Radio.Button>
              <Radio.Button value="map" style={{ lineHeight: '34px' }}>
                <GlobalOutlined style={{ fontSize: '16px' }} />
              </Radio.Button>
            </Radio.Group>
            <Button
              className="ml10"
              disabled={checkPermissionDisabled('gateway:device:query')}
              onClick={() => {
                reload();
              }}
            >
              <ReloadOutlined />
              刷新
            </Button>
          </div>
        </Space>
        {viewMode === 'list' ? (
          <Table
            loading={loading}
            dataSource={dataSource}
            pagination={pagination}
            rowKey="id"
            size="small"
            scroll={{ x: 'max-content' }}
            columns={[
              { title: '设备ID', key: 'id', dataIndex: 'id', fixed: 'left' },
              { title: '设备名称', key: 'name', dataIndex: 'name', fixed: 'left' },
              { title: '所属产品', key: 'product_name', dataIndex: 'product_name' },
              {
                title: '设备状态',
                key: 'status',
                dataIndex: 'status',
                render(value, record) {
                  return (
                    <>
                      <Badge
                        color={
                          {
                            在线: 'green',
                            离线: 'gray',
                            禁用: 'red',
                            未激活: 'orange',
                          }[record.status] || 'gray'
                        }
                      />
                      <Text className="ml5">{value || '未知'}</Text>
                    </>
                  );
                },
              },
              {
                title: '创建时间',
                key: 'created',
                dataIndex: 'created',
                render(value) {
                  return dayjs(value).format('YYYY-MM-DD HH:mm:ss');
                },
              },
              {
                title: '最后上报时间',
                key: 'last_update_ts',
                dataIndex: 'last_update_ts',
                render(value) {
                  return value === 0 ? '-' : dayjs(value).format('YYYY-MM-DD HH:mm:ss');
                },
              },
              {
                title: '操作',
                key: 'action',
                fixed: 'right',
                dataIndex: 'action',
                render(value, record) {
                  return (
                    <Space split={<Text type="secondary">/</Text>}>
                      <Typography.Link
                        onClick={() => {
                          const values = form.getFieldsValue();
                          const driveInstanceId = match.get('drive_instance_id') || '';
                          const query = new URLSearchParams({
                            product_id: values.product_id || '',
                            product_type: values.product_type || '',
                            ...(driveInstanceId ? { drive_instance_id: driveInstanceId } : {}),
                            status: values.status || '',
                            name: values.name || '',
                          }).toString();
                          navigate({ pathname: `/gateway/device/detail/${record?.id}`, search: query ? `?${query}` : '' });
                        }}
                      >
                        详情
                      </Typography.Link>
                      <Typography.Link
                        disabled={checkPermissionDisabled('gateway:device:edit')}
                        onClick={() => {
                          editModalRef.current?.open(record);
                        }}
                      >
                        编辑
                      </Typography.Link>
                      <Popconfirm
                        title="此操作将永久删除该条记录, 是否继续?"
                        okText="确定"
                        cancelText="取消"
                        placement="topRight"
                        disabled={checkPermissionDisabled('gateway:device:remove')}
                        onConfirm={() => {
                          deleteDevice(record?.id).then((resp) => {
                            if (resp.success) {
                              messageApi.open({ type: 'success', content: '删除成功' });
                              reload();
                            } else {
                              messageApi.open({ type: 'error', content: resp.errorMsg });
                            }
                          });
                        }}
                      >
                        <Typography.Link disabled={checkPermissionDisabled('gateway:device:remove')}>删除</Typography.Link>
                      </Popconfirm>
                      {record.node_type === '网关设备' && (
                        <Typography.Link
                          onClick={() => {
                            const values = form.getFieldsValue();
                            const driveInstanceId = match.get('drive_instance_id') || '';
                            const query = new URLSearchParams({
                              product_id: values.product_id || '',
                              product_type: values.product_type || '',
                              ...(driveInstanceId ? { drive_instance_id: driveInstanceId } : {}),
                              status: values.status || '',
                              name: values.name || '',
                              trigger: '子设备触发',
                            }).toString();
                            navigate({ pathname: `/gateway/device/detail/${record?.id}`, search: query ? `?${query}` : '' });
                          }}
                          disabled={checkPermissionDisabled('gateway:device:subdevice')}
                        >
                          子设备({record.child_device_count || 0})
                        </Typography.Link>
                      )}
                    </Space>
                  );
                },
              },
            ]}
          />
        ) : (
          <Spin spinning={loading}>
            {!dataSource?.length && <Empty description={false} />}
            <Row gutter={[24, 24]}>
              {dataSource.map((record: DevicesType) => (
                <Col key={record.id} xs={24} sm={24} md={12} lg={8} xl={8} xxl={8}>
                  <DeviceCard
                    record={record}
                    onDetail={(record) => {
                      const values = form.getFieldsValue();
                      const driveInstanceId = match.get('drive_instance_id') || '';
                      const query = new URLSearchParams({
                        product_id: values.product_id || '',
                        product_type: values.product_type || '',
                        ...(driveInstanceId ? { drive_instance_id: driveInstanceId } : {}),
                        status: values.status || '',
                        name: values.name || '',
                      }).toString();
                      navigate({ pathname: `/gateway/device/detail/${record?.id}`, search: query ? `?${query}` : '' });
                    }}
                    onEdit={(record) => {
                      editModalRef.current?.open(record);
                    }}
                    onDelete={(record) => {
                      deleteDevice(record?.id).then((resp) => {
                        if (resp.success) {
                          messageApi.open({ type: 'success', content: '删除成功' });
                          reload();
                        } else {
                          messageApi.open({ type: 'error', content: resp.errorMsg });
                        }
                      });
                    }}
                    checkPermissionDisabled={checkPermissionDisabled}
                  />
                </Col>
              ))}
            </Row>
            {pagination && (
              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                <Pagination
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={pagination.total}
                  showSizeChanger
                  showQuickJumper
                  showTotal={(total) => `共 ${total} 条`}
                  onChange={(page, pageSize) => {
                    pagination.onChange?.(page, pageSize || 10);
                  }}
                />
              </div>
            )}
          </Spin>
        )}
      </Card>
    </>
  );
};

export default DevicesManage;
